"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import Image from "next/image";
import gsap from "gsap";
import { ArrowUpRightIcon } from "@heroicons/react/24/solid";
import BottomCurves from "../curves/bottomCurves";

export default function AboutMap({
    data,
    isLandingPage = false,
}: {
    data: any;
    // True only when composed into the SG homepage's hero (see
    // aboutContinerSG.tsx) — controls the taller spacing tuned to flow into
    // Brands below, the shared-gradient background opt-out, the map image's
    // fade/dim treatment, and the CTA button. Every other usage of AboutMap
    // (MY or SG, e.g. the about page) keeps the original look.
    isLandingPage?: boolean;
}) {
    const countryRefs = useRef<(HTMLHeadingElement | null)[]>([]);
    const focusedRef = useRef<HTMLDivElement | null>(null);

    // show list of countries
    useGSAP(
        () => {
            countryRefs.current.forEach((el, index) => {
                if (el) {
                    gsap.fromTo(
                        el,
                        { opacity: 0, y: "+=45" },
                        {
                            opacity: 1,
                            y: "0",
                            delay: index * 0.1,
                            duration: 1,
                            ease: "power2.out",
                        },
                    );
                }
            });
        },
        { scope: countryRefs },
    );

    // change malaysia font size
    useGSAP(() => {
        const el = focusedRef.current;
        if (el) {
            gsap.to(el, {
                fontSize: "6.333vw", // adjust this value as needed
                delay: data.countries.length * 0.1 + 0.5,
                duration: 1,
            });
        }
    }, [focusedRef.current]);

    return (
        <section
            className={`relative aspect-[1024/900] lg:aspect-[1280/900] xl:aspect-video w-full flex px-0 ${
                isLandingPage
                    ? "h-auto md:h-[105lvh] pt-16 pb-32 md:pt-28 md:pb-40 xl:pt-[4.719vw] xl:pb-[7.5vw]"
                    : "h-[105lvh] pt-28 pb-28 xl:pt-[4.719vw] xl:pb-[5.333vw]"
            } ${isLandingPage ? "" : "bg-lightGradientPurple"}`}
        >
            {/* <div className="opacity-0 md:opacity-100 z-[1] absolute top-[18%] lg:top-[20%] bottom-0 -left-[31.5%]  lg:-left-[25%] xl:-left-[20.5%] right-0 m-auto w-3 h-3 aspect-square rounded-full bg-purple animate-radar"></div>
            <div className="opacity-0 md:opacity-100 z-[1] absolute top-[23%] lg:top-[25%] bottom-0 -left-[28%] lg:-left-[22%] xl:-left-[18%] right-0 m-auto w-3 h-3 aspect-square rounded-full bg-purple animate-radar"></div> */}
            <picture>
                <source media="(min-width:767px)" srcSet="/HKHD-desktop.gif" />
                <source media="(min-width:480px)" srcSet="/HKHD-tablet.gif" />
                <img
                    src="/HKHD-mobile.gif"
                    alt="Flowers"
                    className={`absolute w-full h-full object-cover top-0 bottom-0 left-0 right-0 m-auto z-1 ${
                        isLandingPage ? "opacity-60 sg-map-fade" : ""
                    }`}
                />
            </picture>
            <div className="md:flex z-[2] w-screen">
                <div className="hidden md:block basis-1/2 px-4">
                    {data.countries.map((country: any, index: number) => (
                        <div
                            ref={(el) => (countryRefs.current[index] = el)}
                            id={`country-` + index}
                            key={`country-` + index}
                        >
                            <h3
                                ref={country.isFocused ? focusedRef : null}
                                className={`text-[3.021vw] font-bold ${
                                    country.isHighlighted
                                        ? "text-white font-extrabold"
                                        : "text-stroke-green text-transparent"
                                }`}
                            >
                                {country.name}
                            </h3>
                        </div>
                    ))}
                </div>
                <div
                    className={`md:basis-1/2 px-7 sm:px-16 xl:px-32 md:pt-32 ${
                        isLandingPage ? "py-10 md:py-[6.217vw]" : "py-[6.217vw]"
                    }`}
                >
                    <h1 className="text-stroke-green text-transparent">
                        {data.heading}
                        <span className="text-[#37DED7]"> KD</span>
                    </h1>
                    <p className="text-white">{data.description}</p>
                    <div className="flex justify-between items-center flex-wrap">
                        <Image
                            src="/h-plus.svg"
                            alt=""
                            width={100}
                            height={100}
                            className=" w-16 md:w-[6.25vw] h-auto aspect-square object-contain"
                        />
                        {isLandingPage && data.ctaLink && data.ctaTitle && (
                            <a
                                href={data.ctaLink}
                                className="inline-flex items-center gap-2 bg-green text-darkGrey font-semibold uppercase text-sm px-7 py-3 rounded-full hover:bg-turquoise transition-colors duration-300"
                            >
                                {data.ctaTitle}
                                <ArrowUpRightIcon className="w-4 h-4" />
                            </a>
                        )}
                    </div>
                </div>
            </div>
            {data.waveBgColor && (
                <BottomCurves
                    color={data.waveBgColor.waveBgColor}
                    direction={"right"}
                />
            )}

            {/* <BannerFloatingArrow
                direction={"right"}
                className={`right-[6vw] -bottom-4 md:-bottom-8`}
            /> */}
        </section>
    );
}
